import axios from 'axios'
import HttpStatus from 'http-status-codes'
import { refreshAccessTokenSuccess } from '../../actions/AuthAction'
import store from '../../Store'
import * as routeService from '../routeService'
import { selectAccessToken } from '../../selectors/authSelector'

const baseUrlV2 = process.env.REACT_APP_API_V2

let isRefreshingAccessToken = false

// Helpers
const getRefreshToken = () => {
  const state = store.getState()
  if (!state.AuthReducer.user || !state.AuthReducer.user.refreshToken) {
    return
  }
  return state.AuthReducer.user.refreshToken
}

let waitingForRefreshingAccessTokenCompletion = []

const waitForRefreshingAccessTokenCompletion = () =>
  new Promise((resolve, reject) => {
    waitingForRefreshingAccessTokenCompletion.push({ resolve, reject })
  })

const refreshAccessToken = async refreshToken => {
  try {
    // Queue retrying request if refresh already in progress
    if (isRefreshingAccessToken) {
      console.warn('Refreshing access token already in progress. Waiting for completion...')
      await waitForRefreshingAccessTokenCompletion()
      // Return the token the completed refresh stored, not a bare `true`: that used to
      // end up in the retried request's Authorization header.
      return selectAccessToken(store.getState())
    }

    console.warn('Refreshing access token started')

    isRefreshingAccessToken = true

    // Refresh call. The v2 API wraps every response body one level deeper, so the
    // tokens live in data.data. Reading the top level stored an empty token over a
    // working one, which left a session that looked logged in but could not load a
    // single protected page.
    const response = await axios.post(`${baseUrlV2}/v2/auth/token-refresh`, {
      refreshToken,
    })
    const { accessToken, refreshToken: newRefreshToken } = (response && response.data && response.data.data) || {}

    // Never keep a session without a token: log out instead of storing half of one.
    // This is a definitive answer from the server, so it ends the session.
    if (!accessToken) {
      throw Object.assign(new Error('Refresh response held no access token'), { isDefinitiveAuthFailure: true })
    }

    // Store new tokens in store
    store.dispatch(refreshAccessTokenSuccess({ accessToken, refreshToken: newRefreshToken }))

    isRefreshingAccessToken = false

    // Resolve & clear queue of pending calls
    waitingForRefreshingAccessTokenCompletion.forEach(completion => {
      completion.resolve()
    })
    waitingForRefreshingAccessTokenCompletion = []

    return accessToken
  } catch (error) {
    console.warn('Refreshing access token failed with error: ', error)

    isRefreshingAccessToken = false

    // Reject & clear queue of pending calls
    waitingForRefreshingAccessTokenCompletion.forEach(completion => {
      completion.reject(error)
    })
    waitingForRefreshingAccessTokenCompletion = []

    // End the session when the server gave a definitive answer: any HTTP response
    // means the refresh token was rejected, and a 200 without a token is just as
    // final. Keeping it would leave the visitor stuck, logged in for the UI and
    // logged out for every guard.
    //
    // A failure with no response at all is a network hiccup (timeout, DNS, TLS): the
    // refresh token may still be perfectly valid, so throwing the session away over
    // it would log someone out for a moment of bad wifi. The call fails, the visitor
    // stays logged in, and the next attempt can still succeed.
    const isDefinitive = Boolean(error && error.response) || Boolean(error && error.isDefinitiveAuthFailure)
    if (isDefinitive) {
      store.dispatch({ type: 'LOGOUT_USER' })
      routeService.goToLoginPage()
    }

    return undefined
  }
}

// Interceptors
const success = response => response

const error = async error => {
  if (error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
    console.warn(`${HttpStatus.UNAUTHORIZED} for request`, error)

    // Get refresh token
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      store.dispatch({ type: 'LOGOUT_USER' })
      routeService.goToLoginPage()

      return Promise.reject(error)
    }

    // Refresh access token
    const newAccessToken = await refreshAccessToken(refreshToken)
    if (!newAccessToken) {
      // Reject rather than resolve with nothing: a silently resolved call let the
      // broken session live on without a single visible error.
      return Promise.reject(error)
    }

    // Retry original call. The two APIs disagree on the header format: the legacy API
    // takes the bare token, the v2 API wants a Bearer prefix. Mirror whatever the
    // original call used, so the retry is not rejected on a perfectly valid token.
    const newConfig = error.config
    const previousHeader = newConfig && newConfig.headers ? newConfig.headers.Authorization : undefined
    const usesBearerPrefix = typeof previousHeader === 'string' && previousHeader.startsWith('Bearer ')
    newConfig.headers.Authorization = usesBearerPrefix ? `Bearer ${newAccessToken}` : newAccessToken
    return Promise.resolve(axios(newConfig))
  }

  return Promise.reject(error)
}

export default {
  success,
  error,
}



// WEBPACK FOOTER //
// ./src/services/interceptors/interceptor.response.refreshAccessToken.js